import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  VehicleGroupsFilterParams,
  VehicleGroupType,
  VehicleGroupsListResponse,
  CreateVehicleGroupParams,
  UpdateVehicleGroupParams
} from '@/types/vehicle-group'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum, SortOrderEnum } from '@/enums'

// API functions
import {
  fetchVehicleGroups,
  fetchVehicleGroup,
  createVehicleGroup,
  updateVehicleGroup,
  deleteVehicleGroup,
  bulkDeleteVehicleGroups,
  sortVehicleGroup
} from '@/lib/api'

type VehicleGroupStateType = {
  data: VehicleGroupType | null
  isLoading: boolean
  error: string | null
}

type VehicleGroupsListStateType = {
  data: VehicleGroupsListResponse | null
  isLoading: boolean
  error: string | null
}

type VehicleGroupsStoreState = {
  slide: VehicleGroupStateType
  slidesList: VehicleGroupsListStateType
  filters: VehicleGroupsFilterParams
  selectedVehicleGroupIds: string[]
  setFilters: (filters: Partial<VehicleGroupsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchVehicleGroups: (params?: VehicleGroupsFilterParams) => Promise<VehicleGroupsListResponse>
  resetVehicleGroupsList: () => void

  // Sort slide
  sortVehicleGroup: (id: string, type: SortOrderEnum) => Promise<VehicleGroupType>

  // Blog Post operations
  getVehicleGroup: (id: string) => Promise<VehicleGroupType>
  createVehicleGroup: (params: CreateVehicleGroupParams) => Promise<VehicleGroupType>
  updateVehicleGroup: (id: string, params: UpdateVehicleGroupParams) => Promise<VehicleGroupType>
  deleteVehicleGroup: (id: string) => Promise<void>
  bulkDeleteVehicleGroups: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectVehicleGroup: (id: string) => void
  deselectVehicleGroup: (id: string) => void
  toggleVehicleGroupSelection: (id: string) => void
  selectAllVehicleGroups: (slides: VehicleGroupType[]) => void
  deselectAllVehicleGroups: () => void
  isVehicleGroupSelected: (id: string) => boolean
  resetVehicleGroup: () => void
}

const defaultFilters: VehicleGroupsFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'order',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const initialVehicleGroupState: VehicleGroupStateType = {
  data: null,
  isLoading: false,
  error: null
}

const initialVehicleGroupsListState: VehicleGroupsListStateType = {
  data: null,
  isLoading: false,
  error: null
}

const useVehicleGroupsStore = create<VehicleGroupsStoreState>()(
  devtools((set, get) => ({
    slide: initialVehicleGroupState,
    slidesList: initialVehicleGroupsListState,
    selectedVehicleGroupIds: [],
    filters: { ...defaultFilters },

    setFilters: newFilters => {
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            delete updatedFilters[key as keyof VehicleGroupsFilterParams]
          } else {
            updatedFilters[key as keyof VehicleGroupsFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      })
    },

    resetFilters: () => set({ filters: { ...defaultFilters } }),

    updateUrlParams: searchParams => {
      const filters: VehicleGroupsFilterParams = {}

      for (const [key, value] of searchParams.entries()) {
        if (!value) {
          continue
        }

        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as VehicleGroupsFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof VehicleGroupsFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getVehicleGroup: async id => {
      set(state => ({
        slide: {
          ...state.slide,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchVehicleGroup(id)

        set(state => ({
          slide: {
            ...state.slide,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          slide: {
            ...state.slide,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createVehicleGroup: async params => {
      set(state => ({
        slide: {
          ...state.slide,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createVehicleGroup(params)

        set(state => ({
          slide: {
            ...state.slide,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          slide: {
            ...state.slide,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    updateVehicleGroup: async (id, params) => {
      set(state => ({
        slide: {
          ...state.slide,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await updateVehicleGroup(id, params)

        set(state => ({
          slide: {
            ...state.slide,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          slide: {
            ...state.slide,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deleteVehicleGroup: async id => {
      set(state => ({
        slide: {
          ...state.slide,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deleteVehicleGroup(id)

        set(state => ({
          slide: {
            ...state.slide,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          slide: {
            ...state.slide,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetVehicleGroup: () => {
      set(state => ({
        slide: {
          ...state.slide,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectVehicleGroup: id => {
      set(state => {
        if (state.selectedVehicleGroupIds.includes(id)) {
          return state
        }
        return { selectedVehicleGroupIds: [...state.selectedVehicleGroupIds, id] }
      })
    },

    deselectVehicleGroup: id => {
      set(state => ({
        selectedVehicleGroupIds: state.selectedVehicleGroupIds.filter(slideId => slideId !== id)
      }))
    },

    toggleVehicleGroupSelection: id => {
      set(state => {
        if (state.selectedVehicleGroupIds.includes(id)) {
          return {
            selectedVehicleGroupIds: state.selectedVehicleGroupIds.filter(slideId => slideId !== id)
          }
        }
        return { selectedVehicleGroupIds: [...state.selectedVehicleGroupIds, id] }
      })
    },

    selectAllVehicleGroups: slides => {
      set(() => ({
        selectedVehicleGroupIds: slides.map(slide => slide.id)
      }))
    },

    deselectAllVehicleGroups: () => {
      set(() => ({ selectedVehicleGroupIds: [] }))
    },

    isVehicleGroupSelected: id => {
      return get().selectedVehicleGroupIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteVehicleGroups: async ids => {
      set(state => ({
        slide: {
          ...state.slide,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteVehicleGroups({ ids })

        set(state => ({
          slide: {
            ...state.slide,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedVehicleGroupIds: state.selectedVehicleGroupIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          slide: {
            ...state.slide,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchVehicleGroups: async (params?: VehicleGroupsFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        slidesList: {
          ...state.slidesList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchVehicleGroups(filtersToUse)

        set(state => ({
          slidesList: {
            ...state.slidesList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          slidesList: {
            ...state.slidesList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetVehicleGroupsList: () => {
      set(state => ({
        slidesList: {
          ...state.slidesList,
          data: null,
          error: null
        }
      }))
    },

    sortVehicleGroup: async (id, type) => {
      set(() => ({
        slide: {
          ...get().slide,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await sortVehicleGroup(id, type)

        set(() => ({
          slide: {
            ...get().slide,
            isLoading: false,
            data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          slide: {
            ...initialVehicleGroupState,
            error: getErrorMessage(error)
          }
        }))
        throw error
      }
    }
  }))
)

export default useVehicleGroupsStore
